import React, { useState, useEffect } from 'react';
import { Tag, Plus, Edit2, X, Save, Loader2, Percent, DollarSign, Calendar, Hash } from 'lucide-react';
import { auth } from '../config/firebase';
import { cupomService, servicoService } from '../services/api';

const formatarData = (iso) => {
  if (!iso) return null;
  const [ano, mes, dia] = iso.split('T')[0].split('-');
  return `${dia}/${mes}/${ano}`;
};

const CuponsTab = () => {
  const [cupons, setCupons] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    codigo: '', descricao: '', tipo_desconto: 'percentual', valor_desconto: '',
    servico_id: '', data_validade: '', limite_uso: '', ativo: true,
  });

  const carregarDados = async () => {
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      const [resCupons, resServicos] = await Promise.all([
        cupomService.listar(token),
        servicoService.listar(user.uid),
      ]);
      setCupons(resCupons.data || []);
      setServicos(resServicos.data || []);
    } catch (error) {
      console.error('Erro ao carregar cupons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const abrirModal = (cupom = null) => {
    if (cupom) {
      setEditingId(cupom.id);
      setForm({
        codigo: cupom.codigo,
        descricao: cupom.descricao || '',
        tipo_desconto: cupom.tipo_desconto,
        valor_desconto: cupom.valor_desconto,
        servico_id: cupom.servico_id || '',
        data_validade: cupom.data_validade ? cupom.data_validade.split('T')[0] : '',
        limite_uso: cupom.limite_uso || '',
        ativo: cupom.ativo,
      });
    } else {
      setEditingId(null);
      setForm({ codigo: '', descricao: '', tipo_desconto: 'percentual', valor_desconto: '', servico_id: '', data_validade: '', limite_uso: '', ativo: true });
    }
    setIsModalOpen(true);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const payload = {
        codigo: form.codigo,
        descricao: form.descricao || null,
        tipo_desconto: form.tipo_desconto,
        valor_desconto: parseFloat(form.valor_desconto),
        servico_id: form.servico_id || null,
        data_validade: form.data_validade || null,
        limite_uso: form.limite_uso ? parseInt(form.limite_uso, 10) : null,
        ativo: form.ativo,
      };

      if (editingId) {
        await cupomService.atualizar(editingId, payload, token);
      } else {
        await cupomService.criar(payload, token);
      }

      await carregarDados();
      setIsModalOpen(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao salvar cupom.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="animate-spin text-[#1883FF]" size={24} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 border-b border-purple-50 pb-4">
        <div className="flex items-center gap-3">
          <Tag size={18} className="text-[#1883FF]" />
          <div>
            <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Cupons</h3>
            <p className="text-[10px] font-semibold text-slate-500 m-0 mt-1">Códigos de desconto aplicados de verdade no preço na agenda pública.</p>
          </div>
        </div>
        <button
          onClick={() => abrirModal()}
          className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider cursor-pointer flex items-center gap-2 hover:opacity-90 shadow-sm shadow-[#6D28D9]/20 transition-all shrink-0"
        >
          <Plus size={14} /> Novo Cupom
        </button>
      </div>

      {cupons.length === 0 ? (
        <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-2xl p-10 flex flex-col items-center justify-center text-center">
          <Tag size={32} className="text-slate-300 mb-3" />
          <p className="text-sm font-bold text-slate-500 mb-1">Nenhum cupom cadastrado.</p>
          <p className="text-[10px] font-medium text-slate-400">Clique no botão acima para criar seu primeiro cupom de desconto.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cupons.map((cupom) => (
            <div key={cupom.id} className="bg-white border border-slate-100 p-5 rounded-2xl shadow-sm flex flex-col justify-between hover:border-purple-100 hover:shadow-md transition-all">
              <div>
                <div className="flex justify-between items-start gap-2 mb-2">
                  <h4 className="m-0 text-sm font-black text-slate-800 leading-tight tracking-wide">{cupom.codigo}</h4>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase ${cupom.ativo ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                      {cupom.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                    <button onClick={() => abrirModal(cupom)} className="bg-transparent border-none p-1.5 text-slate-400 hover:text-[#1883FF] hover:bg-[#F5F3FF] rounded-lg cursor-pointer transition-colors"><Edit2 size={14} /></button>
                  </div>
                </div>

                {cupom.descricao && (
                  <p className="text-xs text-slate-500 font-medium mb-3 leading-relaxed">{cupom.descricao}</p>
                )}

                <p className="text-xs font-bold text-slate-600 mb-1">
                  {cupom.nome_servico || 'Todos os serviços'}
                </p>
                {cupom.data_validade && (
                  <p className="flex items-center gap-1.5 text-[10px] text-slate-400 font-semibold">
                    <Calendar size={11} /> Válido até {formatarData(cupom.data_validade)}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-50">
                <span className="flex items-center gap-1 bg-[#F8F7FF] text-[#6D28D9] px-2 py-1 rounded-md text-[10px] font-bold">
                  {cupom.tipo_desconto === 'fixo' ? <DollarSign size={12} /> : <Percent size={12} />}
                  {cupom.tipo_desconto === 'fixo' ? `R$ ${Number(cupom.valor_desconto).toFixed(2).replace('.', ',')}` : `${Number(cupom.valor_desconto)}%`} OFF
                </span>
                <span className="flex items-center gap-1 bg-slate-50 text-slate-500 px-2 py-1 rounded-md text-[10px] font-bold">
                  <Hash size={12} /> {cupom.usos_atuais} {cupom.limite_uso ? `/ ${cupom.limite_uso}` : ''} usos
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[500px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0 flex items-center gap-2">
                <Tag size={18} className="text-[#1883FF]" />
                {editingId ? 'Editar Cupom' : 'Novo Cupom'}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setIsModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSalvar} className="flex flex-col gap-4 overflow-y-auto pr-1">
              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Código *</label>
                <input
                  required type="text"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 uppercase outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  placeholder="Ex: VERAO20"
                  value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Descrição</label>
                <input
                  type="text"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  placeholder="Ex: Promoção de verão"
                  value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Tipo de Desconto</label>
                  <select
                    className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-700 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 cursor-pointer"
                    value={form.tipo_desconto} onChange={e => setForm({ ...form, tipo_desconto: e.target.value })}
                  >
                    <option value="percentual">Percentual (%)</option>
                    <option value="fixo">Valor Fixo (R$)</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Valor *</label>
                  <input
                    required type="number" step="0.01" min="0"
                    className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                    placeholder={form.tipo_desconto === 'fixo' ? 'Ex: 10.00' : 'Ex: 20'}
                    value={form.valor_desconto} onChange={e => setForm({ ...form, valor_desconto: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Serviço</label>
                <select
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-700 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 cursor-pointer"
                  value={form.servico_id} onChange={e => setForm({ ...form, servico_id: e.target.value })}
                >
                  <option value="">Todos os serviços</option>
                  {servicos.map(s => <option key={s.id} value={s.id}>{s.nome_servico}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Validade</label>
                  <input
                    type="date"
                    className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                    value={form.data_validade} onChange={e => setForm({ ...form, data_validade: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Limite de Uso</label>
                  <input
                    type="number" min="1"
                    className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                    placeholder="Sem limite"
                    value={form.limite_uso} onChange={e => setForm({ ...form, limite_uso: e.target.value })}
                  />
                </div>
              </div>

              {editingId && (
                <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} />
                  Cupom ativo
                </label>
              )}

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none py-4 mt-4 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isSubmitting ? 'SALVANDO...' : 'SALVAR CUPOM'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuponsTab;
